import { createSignal, type JSX, Show } from 'solid-js';
import { t } from '../../i18n';
import { ApiError } from '../../lib/api';
import { createMutation } from '../../lib/mutation';
import { sendTestEmail } from '../../lib/notification-settings';
import { Button } from '../ui/Button';

// "Send me a test email" (notifications overhaul, slice 20): proves the address
// works without leaving the page. One POST and no read, so nothing to re-read.
//
// THE ONE PLACE THIS PAGE DOES NOT SHOW THE API'S OWN `detail`. The house rule
// (`apps/dashboard/CLAUDE.md` § Boundary) is that a refusal is the server's
// sentence. Here the four refusals are a closed set the page says better in its
// own words and its own locale ("no address on file", "this address bounced"),
// and the server's `detail` for them is English API prose. So they are chosen by
// STATUS (`createMutation`'s `onError` hands over the cause for exactly this,
// `lib/mutation.ts`), and only a status outside the set falls back to the
// server's sentence.

/** Refusal status -> the page's sentence. The route answers these four and no others. */
const REFUSAL_KEY: Readonly<Record<number, string>> = {
  422: 'notifications_settings.send_to.test_no_address',
  409: 'notifications_settings.send_to.test_bounced',
  429: 'notifications_settings.send_to.test_too_many',
  502: 'notifications_settings.send_to.test_failed',
};

type Outcome =
  | { readonly kind: 'idle' }
  | { readonly kind: 'sent'; readonly address: string }
  | { readonly kind: 'refused'; readonly message: string };

/** The mapped sentence for a known refusal; `fallback` (server detail, else generic) otherwise. */
function refusalMessage(cause: unknown, fallback: string): string {
  const key = cause instanceof ApiError ? REFUSAL_KEY[cause.status] : undefined;
  return key === undefined ? fallback : t(key);
}

export function NotificationTestEmail(props: { token: string | null }): JSX.Element {
  const [outcome, setOutcome] = createSignal<Outcome>({ kind: 'idle' });

  const send = createMutation({
    call: () => sendTestEmail(props.token),
    fallback: t('notifications_settings.send_to.test_error'),
    onSuccess: (result) => setOutcome({ kind: 'sent', address: result.sent_to }),
    onError: (message, cause) =>
      setOutcome({ kind: 'refused', message: refusalMessage(cause, message) }),
  });

  const sentTo = (): string | null => {
    const o = outcome();
    return o.kind === 'sent' ? o.address : null;
  };
  const refused = (): string | null => {
    const o = outcome();
    return o.kind === 'refused' ? o.message : null;
  };

  return (
    <div class="notification-test-email">
      <Button
        variant="outline"
        size="sm"
        disabled={send.pending()}
        onClick={() => {
          setOutcome({ kind: 'idle' });
          void send.run();
        }}
      >
        {t('notifications_settings.send_to.test_button')}
      </Button>
      <Show when={sentTo()}>
        {(address) => (
          <p class="notification-test-email__sent" role="status">
            {t('notifications_settings.send_to.test_sent', { address: address() })}
          </p>
        )}
      </Show>
      <Show when={refused()}>
        {(message) => (
          <p class="field__error" role="alert">
            {message()}
          </p>
        )}
      </Show>
    </div>
  );
}
